import { useEffect, useState, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { channelsApi } from '@/api/channels';
import { useChatStore } from '@/store/chatStore';
import ChannelSidebar from '@/components/chat/ChannelSidebar';
import TopBar from '@/components/chat/TopBar';
import ChannelView from '@/components/chat/ChannelView';
import DMView from '@/components/chat/DMView';
import SettingsPage from '@/pages/chat/SettingsPage';
import LibraryPage from '@/pages/chat/LibraryPage';
import SecretChatPage from '@/pages/chat/SecretChatPage';
import CallHistoryPage from '@/pages/chat/CallHistoryPage';
import BotsPage from '@/pages/chat/BotsPage';
import MiniAppsPage from '@/pages/chat/MiniAppsPage';
import MiniAppsManagePage from '@/pages/chat/MiniAppsManagePage';
import ProfilePage from '@/pages/profile/ProfilePage';
import ShopPage from '@/pages/shop/ShopPage';
import { Hash } from 'lucide-react';
import MobileBottomNav from '@/components/chat/MobileBottomNav';
import { wireOutboxAutoFlush } from '@/lib/outboxFlush';
import { useQueryClient } from '@tanstack/react-query';

// Mobile context — sidebar visibility
interface MobileCtx {
  showSidebar: boolean;
  setShowSidebar: (v: boolean) => void;
}
const MobileContext = createContext<MobileCtx>({ showSidebar: true, setShowSidebar: () => {} });
export const useMobile = () => useContext(MobileContext);

function WelcomeScreen() {
  return (
    <div className="flex-1 flex items-center justify-center text-center">
      <div>
        <Hash size={56} className="mx-auto mb-4 text-brand-500 opacity-40" />
        <h2 className="text-xl font-bold text-surface-300 mb-2">Добро пожаловать в swipa</h2>
        <p className="text-surface-500 text-sm">Выберите канал в боковой панели</p>
      </div>
    </div>
  );
}

export default function ChatPage() {
  const { setJoinedChannels } = useChatStore();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Phase 11.4 — drain the offline outbox whenever the browser comes back
  // online. Wired once per page lifetime; handler is idempotent.
  useEffect(() => {
    wireOutboxAutoFlush((r) => {
      if (r.sent > 0) {
        // Refresh both channel and DM message lists
        queryClient.invalidateQueries({ queryKey: ['messages'] });
      }
    });
  }, [queryClient]);

  // On mobile, sidebar is shown when at root, hidden when viewing a channel
  const isAtRoot = location.pathname === '/' || location.pathname === '';
  const [showSidebar, setShowSidebar] = useState(true);

  // Auto-show sidebar when navigating to root
  useEffect(() => {
    if (isAtRoot) setShowSidebar(true);
  }, [isAtRoot]);

  const { data } = useQuery({
    queryKey: ['channels', 'mine'],
    queryFn: () => channelsApi.listMine(),
  });

  useEffect(() => {
    if (Array.isArray(data)) setJoinedChannels(data);
  }, [data, setJoinedChannels]);

  return (
    <MobileContext.Provider value={{ showSidebar, setShowSidebar }}>
      <div className="flex flex-col h-[100dvh] bg-surface-950 overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <TopBar />
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Sidebar: always visible on desktop, toggle on mobile */}
          <div className={`
            ${showSidebar ? 'flex' : 'hidden'}
            md:flex
            w-full md:w-60
            flex-shrink-0
          `}>
            <ChannelSidebar />
          </div>

          {/* Main: always visible on desktop, hidden when sidebar shown on mobile */}
          <main className={`
            ${showSidebar ? 'hidden' : 'flex'}
            md:flex
            flex-1 min-w-0 overflow-hidden
          `}>
            <Routes>
              <Route index element={<WelcomeScreen />} />
              <Route path="channel/:channelId" element={<ChannelView />} />
              <Route path="dm/:userId" element={<DMView />} />
              <Route path="dm" element={<DMListView />} />
              <Route path="secret/:chatId" element={<SecretChatPage />} />
              <Route path="calls" element={<CallHistoryPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="library" element={<LibraryPage />} />
              <Route path="bots" element={<BotsPage />} />
              <Route path="miniapps" element={<MiniAppsPage />} />
              <Route path="miniapps/manage" element={<MiniAppsManagePage />} />
              <Route path="u/:username" element={<ProfilePage />} />
              <Route path="profile/:username" element={<ProfilePage />} />
              <Route path="shop" element={<ShopPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>

        {/* Mobile bottom navigation — hidden on desktop */}
        <MobileBottomNav />
      </div>
    </MobileContext.Provider>
  );
}

function DMListView() {
  const navigate = useNavigate();
  const { setShowSidebar } = useMobile();
  const { data } = useQuery({
    queryKey: ['dm-conversations'],
    queryFn: () => import('@/api/messages').then((m) => m.messagesApi.getDMConversations()),
  });

  return (
    <div className="flex-1 flex flex-col">
      <div className="h-12 border-b border-surface-800 px-4 flex items-center gap-3">
        <button
          onClick={() => { setShowSidebar(true); navigate('/'); }}
          className="md:hidden btn-ghost p-1.5 text-surface-400"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <span className="font-semibold text-surface-200">Личные сообщения</span>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {data?.length === 0 && (
          <p className="text-surface-500 text-sm text-center mt-8">Пока нет диалогов</p>
        )}
        {data?.map((conv) =>
          conv.partner ? (
            <button
              key={conv.partner.id}
              onClick={() => { setShowSidebar(false); navigate(`/dm/${conv.partner!.id}`); }}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-surface-800 transition-colors w-full text-left"
            >
              <div className="w-9 h-9 rounded-full bg-brand-700 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                {conv.partner.username.slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-surface-100 text-sm font-medium truncate">
                  {conv.partner.displayName}
                </p>
                <p className="text-surface-500 text-xs truncate font-mono">@{conv.partner.username}</p>
              </div>
              {conv.unreadCount > 0 && (
                <span className="badge bg-brand-600 text-white">
                  {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                </span>
              )}
            </button>
          ) : null,
        )}
      </div>
    </div>
  );
}
